import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Building2, User as UserIcon, Mail, Phone, Sparkles, Lock, Megaphone, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Turnstile } from "@/components/Turnstile";

const PLAN_LABELS = {
  "basic-broadcaster": "Basic Broadcaster — $49.95/mo",
  "media-ministry-hub": "Media Ministry Hub — $59.95/mo",
  "premium-services": "Premium Services — $89.95/mo",
  "ascend-ultra": "Ascend Ultra — $99.95/mo",
};

const HEARD_OPTIONS = [
  "Google Search",
  "Referral from a friend",
  "Social Media",
  "Church Conference",
  "YouTube",
  "Email / Newsletter",
  "Other",
];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan") || "";
  const planLabel = PLAN_LABELS[plan] || "";
  const [form, setForm] = useState({ name: "", email: "", password: "", organization: "", phone: "", heard_from: "" });
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.organization.trim() || !form.password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!form.heard_from) {
      toast.error("Please tell us how you heard about us");
      return;
    }
    // Only block on the CAPTCHA while it's still working. If it failed to load
    // (ad-blocker / domain not allow-listed), we let the signup through — every
    // new account is Pending and reviewed by an admin anyway.
    if (!captchaToken && !captchaFailed) {
      toast.error("Please complete the security check above");
      return;
    }
    setSubmitting(true);
    try {
      await register({ ...form, plan, turnstile_token: captchaToken });
      toast.success("Account created! Your trial is active pending admin approval.");
      navigate("/streams");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Registration failed. Please try again.");
      setCaptchaToken("");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grid flex flex-col items-center justify-center p-6">
      <button data-testid="back-home" onClick={() => navigate("/")} className="absolute top-6 left-6 flex items-center gap-2 text-sm text-gray-400 hover:text-amber-400 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back home
      </button>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-card border border-amber-500/20 rounded-2xl p-8 sm:p-10 shadow-[0_0_60px_rgba(245,158,11,0.06)] my-10">
        <div className="flex items-center gap-3 mb-6">
          <img src="/streamworship-mark.png" alt="StreamWorship" className="h-11 w-11 object-contain" />
          <p className="font-display font-bold text-lg">Stream<span className="text-gold-gradient">Worship</span></p>
        </div>
        <h1 className="font-display text-2xl font-bold">Start your 14-day free trial</h1>
        <p className="text-gray-400 text-sm mt-2 mb-5">Create your account to launch your broadcast control panel.</p>

        {planLabel && (
          <div data-testid="selected-plan" className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/[0.07] px-4 py-3 flex items-center gap-3">
            <span className="h-8 w-8 rounded-lg bg-amber-500/15 border border-amber-500/25 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4 text-amber-400" />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">Selected plan</p>
              <p className="text-sm font-semibold text-amber-300">{planLabel}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Full Name</Label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pastor Dave Miller" className="pl-10 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@church.org" className="pl-10 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="pl-10 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Church / Organization</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input data-testid="register-organization" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="Grace Community Chapel" className="pl-10 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Phone <span className="text-gray-600 font-normal">(optional)</span></Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input data-testid="register-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="pl-10 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">How did you hear about us?</Label>
            <div className="relative">
              <Megaphone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none z-10" />
              <select
                data-testid="register-heard-from"
                value={form.heard_from}
                onChange={(e) => setForm({ ...form, heard_from: e.target.value })}
                className="w-full pl-10 pr-4 h-11 rounded-md bg-gray-950 border border-gray-800 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 appearance-none cursor-pointer"
              >
                <option value="" disabled className="text-gray-500">Select an option…</option>
                {HEARD_OPTIONS.map((o) => (
                  <option key={o} value={o} className="bg-gray-950 text-white">{o}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2 pt-1">
            <Label className="text-gray-300 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-amber-400" /> Security check</Label>
            <Turnstile
              onVerify={(t) => { setCaptchaToken(t); setCaptchaFailed(false); }}
              onExpire={() => setCaptchaToken("")}
              onError={() => { setCaptchaToken(""); setCaptchaFailed(true); }}
            />
            {captchaFailed && (
              <p data-testid="captcha-fallback-note" className="text-[11px] text-gray-500 leading-relaxed">
                Couldn't load the security check (it may be blocked by your browser or network). You can still continue — new accounts are reviewed by our team before activation.
              </p>
            )}
          </div>
        </div>

        <button data-testid="register-submit" onClick={submit} disabled={submitting} className="mt-7 w-full gold-gradient hover:brightness-110 text-black font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {submitting ? "Creating account…" : "Create account & start trial"}
        </button>

        <p className="text-xs text-gray-500 text-center mt-5 leading-relaxed">
          New organizations start their <span className="text-amber-400 font-medium">14-day free trial</span> as <span className="text-amber-400 font-medium">Pending</span> and are activated once an administrator approves your account.
        </p>
        <p className="text-sm text-gray-400 text-center mt-4">
          Already have an account? <button data-testid="to-login" onClick={() => navigate("/login")} className="text-amber-400 hover:underline">Sign in</button>
        </p>
        <p className="text-xs text-gray-600 text-center mt-3">
          By signing up you agree to our <a data-testid="register-privacy" href="/privacy" className="text-amber-400/80 hover:underline">Privacy Policy</a> and <a data-testid="register-terms" href="/terms" className="text-amber-400/80 hover:underline">Terms of Service</a>.
        </p>
      </motion.div>
    </div>
  );
}
